import React, {useCallback, useEffect, useState} from 'react';
import {addDays, format, isValid, startOfDay} from 'date-fns';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Heading from '../molcules/Heading/Heading';
import {
  assignPlandayDepartment,
  getPlandayAuthorizationUrl,
  getPlandayDepartments,
  importPlandaySchedule,
  importPlandayPayArrangements,
  getPlandayShifts,
  getPlandayStatus,
  importPlandayShifts
} from './api';

export default function Planday() {
  const [startDate, setStartDate] = useState(() => startOfDay(new Date()));
  const [endDate, setEndDate] = useState(() => addDays(startOfDay(new Date()), 1));
  const validRange = isValid(startDate) && isValid(endDate) && startOfDay(startDate) <= startOfDay(endDate);
  const dateRange = () => ({startDate: format(startDate, 'yyyy-MM-dd'), endDate: format(endDate, 'yyyy-MM-dd')});
  const changeDate = setter => date => {
    setter(date);
    setShifts(null);
    setImportResult(null);
    setShiftsError('');
  };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [payError, setPayError] = useState('');
  const [shifts, setShifts] = useState(null);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsError, setShiftsError] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState('');
  const [savingDepartmentId, setSavingDepartmentId] = useState(null);
  const result = new URLSearchParams(window.location.search).get('planday');

  const loadDepartments = useCallback(() => {
    setDepartmentsLoading(true);
    setDepartmentsError('');
    getPlandayDepartments()
      .then(response => {
        setDepartments(response.data.departments || []);
        setSites(response.data.sites || []);
        setDepartmentsLoading(false);
      })
      .catch(requestError => {
        setDepartmentsError(requestError.errorArray?.join(', ') || 'Unable to get departments from Planday. Reconnect if the department permission has not yet been granted.');
        setDepartmentsLoading(false);
      });
  }, []);

  useEffect(() => {
    getPlandayStatus()
      .then(response => {
        setStatus(response.data);
        setLoading(false);
        if (response.data.connected) loadDepartments();
      })
      .catch(() => {
        setError('Unable to check the Planday connection.');
        setLoading(false);
      });
  }, [loadDepartments]);

  const connect = () => {
    setLoading(true);
    setError('');
    getPlandayAuthorizationUrl()
      .then(response => {
        window.location.assign(response.data.url);
      })
      .catch(() => {
        setError('Unable to start the Planday connection.');
        setLoading(false);
      });
  };

  const importSchedule = () => {
    setScheduleLoading(true);
    setScheduleError('');
    setSchedule(null);
    importPlandaySchedule()
      .then(response => {
        setSchedule(response.data);
        setScheduleLoading(false);
      })
      .catch(requestError => {
        setScheduleError(requestError.errorArray?.join(', ') || 'Unable to import the schedule from Planday.');
        setScheduleLoading(false);
      });
  };

  const loadShifts = () => {
    if (!validRange) return;
    setShiftsLoading(true);
    setShiftsError('');
    getPlandayShifts(dateRange())
      .then(response => {
        setShifts(response.data);
        setShiftsLoading(false);
      })
      .catch(requestError => {
        setShiftsError(requestError.errorArray?.join(', ') || 'Unable to get shifts from Planday.');
        setShiftsLoading(false);
      });
  };

  const importPayArrangements = () => {
    setPayLoading(true);
    setPayResult(null);
    setPayError('');
    importPlandayPayArrangements()
      .then(response => setPayResult(response.data))
      .catch(requestError => {
        setPayError(requestError.errorArray?.join(', ') || 'Unable to import pay arrangements from Planday.');
      })
      .finally(() => setPayLoading(false));
  };

  const importShifts = () => {
    if (!validRange) return;
    setImportLoading(true);
    setImportResult(null);
    setShiftsError('');
    importPlandayShifts(dateRange())
      .then(response => {
        setImportResult(response.data);
        setImportLoading(false);
      })
      .catch(requestError => {
        setShiftsError(requestError.errorArray?.join(', ') || 'Unable to import shifts from Planday.');
        setImportLoading(false);
      });
  };

  const assignDepartment = (departmentId, siteId, options = {}) => {
    setSavingDepartmentId(departmentId);
    setDepartmentsError('');
    assignPlandayDepartment(departmentId, siteId, options)
      .then(response => {
        setDepartments(current => current.map(department => (
          department.id.toString() === departmentId.toString()
            ? {...department, ...response.data}
            : department
        )));
        setSavingDepartmentId(null);
      })
      .catch(requestError => {
        setDepartmentsError(requestError.errorArray?.join(', ') || 'Unable to save the department assignment.');
        setSavingDepartmentId(null);
      });
  };

  return (
    <div>
      <Heading title="Planday" />
      <Paper sx={{maxWidth: 900, padding: 3}}>
        {result === 'connected' && <Alert severity="success" sx={{marginBottom: 2}}>Planday was connected successfully.</Alert>}
        {result === 'cancelled' && <Alert severity="info" sx={{marginBottom: 2}}>Planday authorisation was cancelled.</Alert>}
        {result === 'error' && <Alert severity="error" sx={{marginBottom: 2}}>Planday could not be connected. Please try again.</Alert>}
        {error && <Alert severity="error" sx={{marginBottom: 2}}>{error}</Alert>}

        <Typography variant="h6" gutterBottom>Planday integration</Typography>
        <Typography paragraph>
          Connect the application to its Planday portal. This single connection is shared across all sites.
        </Typography>
        {status && status.connected && (
          <Alert severity="success" sx={{marginBottom: 2}}>Planday is connected.</Alert>
        )}
        <Button
          variant="contained"
          onClick={connect}
          disabled={loading}
        >
          {status && status.connected ? 'Reconnect Planday' : 'Connect Planday'}
        </Button>
      </Paper>

      <Paper sx={{maxWidth: 900, padding: 3, marginTop: 3}}>
        <Typography variant="h6" gutterBottom>Department mappings</Typography>
        <Typography paragraph>
          Assign each Planday department to a site and choose whether its shifts import to rota, presence, or both. Multiple departments can map to the same site.
          {' '}These options apply when you import shifts or the schedule.
        </Typography>
        {departmentsError && <Alert severity="error" sx={{marginBottom: 2}}>{departmentsError}</Alert>}
        <Button
          variant="outlined"
          onClick={loadDepartments}
          disabled={departmentsLoading || !status?.connected}
          sx={{marginBottom: 2}}
        >
          {departmentsLoading ? 'Getting departments...' : 'Refresh departments'}
        </Button>
        {departments.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Planday department</TableCell>
                  <TableCell>Department ID</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Rota</TableCell>
                  <TableCell>Presence</TableCell>
                  <TableCell>Default presence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.map(department => (
                  <TableRow key={department.id}>
                    <TableCell>{department.name}</TableCell>
                    <TableCell>{department.id}</TableCell>
                    <TableCell sx={{width: 320}}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={department.siteId || ''}
                          displayEmpty
                          disabled={savingDepartmentId !== null}
                          onChange={event => assignDepartment(department.id, event.target.value)}
                        >
                          <MenuItem value=""><em>Unassigned</em></MenuItem>
                          {sites.map(site => (
                            <MenuItem key={site._id} value={site._id}>{site.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Switch checked={department.rota !== false}
                        slotProps={{input: {'aria-label': `Import ${department.name} to rota`}}}
                        disabled={!department.siteId || savingDepartmentId !== null}
                        onChange={event => assignDepartment(department.id, department.siteId, {rota: event.target.checked})} />
                    </TableCell>
                    <TableCell>
                      <Switch checked={department.presence === true}
                        slotProps={{input: {'aria-label': `Import ${department.name} to presence`}}}
                        disabled={!department.siteId || savingDepartmentId !== null}
                        onChange={event => assignDepartment(department.id, department.siteId, {presence: event.target.checked})} />
                    </TableCell>
                    <TableCell sx={{minWidth: 140}}>
                      <FormControl fullWidth size="small">
                        <Select value={department.defaultPresenceStatus || 'on-site'}
                          inputProps={{'aria-label': `Default presence for ${department.name}`}}
                          disabled={!department.siteId || !department.presence || savingDepartmentId !== null}
                          onChange={event => assignDepartment(department.id, department.siteId, {defaultPresenceStatus: event.target.value})}>
                          <MenuItem value="on-site">On-Site</MenuItem>
                          <MenuItem value="off-site">Off-Site</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{maxWidth: 900, padding: 3, marginTop: 3}}>
        <Typography variant="h6" gutterBottom>Pay arrangements</Typography>
        <Typography paragraph>
          Refresh whether staff are hourly paid, salaried or paid per shift. Manual settings are preserved.
          {' '}New staff are added from departments mapped to a site with rota or presence enabled.
        </Typography>
        <Typography paragraph color="text.secondary">
          Reconnect Planday if pay permissions have not yet been granted. This import can take a little time for larger teams.
        </Typography>
        {payError && <Alert severity="error" sx={{marginBottom: 2}}>{payError}</Alert>}
        <Button variant="contained" onClick={importPayArrangements}
          disabled={payLoading || scheduleLoading || importLoading || !status?.connected}>
          {payLoading ? 'Importing pay arrangements...' : 'Import pay arrangements'}
        </Button>
        {payResult !== null && (
          <Alert severity="success" sx={{marginTop: 2}}>
            Updated pay arrangements for {payResult.updated || 0} staff. Created {payResult.usersCreated || 0} staff users.
            <div>
              Hourly: {payResult.types?.hourly || 0}. Salaried: {payResult.types?.salary || 0}.
              {' '}Per shift: {payResult.types?.['per-shift'] || 0}. Mixed: {payResult.types?.mixed || 0}.
              {' '}Unknown: {payResult.types?.unknown || 0}.
            </div>
            {payResult.skippedManual > 0 && <div>Preserved {payResult.skippedManual} manual or other-source arrangements.</div>}
            {payResult.skippedUnmapped > 0 && <div>Skipped {payResult.skippedUnmapped} staff without a mapped department.</div>}
            {payResult.unavailable > 0 && <div>Pay information was unavailable for {payResult.unavailable} staff; their type is unknown.</div>}
            {payResult.skippedConflictingUser > 0 && <div>Skipped {payResult.skippedConflictingUser} conflicting staff matches.</div>}
            {payResult.skippedConcurrent > 0 && <div>Skipped {payResult.skippedConcurrent} staff changed during the import.</div>}
          </Alert>
        )}
      </Paper>

      <Paper sx={{maxWidth: 900, padding: 3, marginTop: 3}}>
        <Typography variant="h6" gutterBottom>Latest shifts</Typography>
        <Typography paragraph>
          Fetch or import all shifts within the selected date range, including matching employee details and punch times.
          {' '}Large date ranges can take longer because Planday requests are paced to respect rate limits.
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 2}}>
            <DatePicker label="Start date" format="dd/MM/yyyy" value={startDate}
              onChange={changeDate(setStartDate)} disabled={shiftsLoading || importLoading} />
            <DatePicker label="End date" format="dd/MM/yyyy" value={endDate} minDate={isValid(startDate) ? startDate : undefined}
              onChange={changeDate(setEndDate)} disabled={shiftsLoading || importLoading} />
          </Box>
        </LocalizationProvider>
        {!validRange && <Alert severity="error" sx={{marginBottom: 2}}>Choose valid dates with the end date on or after the start date.</Alert>}
        {shiftsError && <Alert severity="error" sx={{marginBottom: 2}}>{shiftsError}</Alert>}
        <Button
          variant="outlined"
          onClick={loadShifts}
          disabled={shiftsLoading || importLoading || !validRange || !status?.connected}
        >
          {shiftsLoading ? 'Getting shifts...' : 'Get shifts'}
        </Button>
        <Button
          variant="contained"
          onClick={importShifts}
          disabled={importLoading || scheduleLoading || payLoading || shiftsLoading || !validRange || !status?.connected}
          sx={{marginLeft: 2}}
        >
          {importLoading ? 'Importing shifts...' : 'Import shifts'}
        </Button>
        {importResult && (
          <Alert severity="success" sx={{marginTop: 2}}>
            Rota: {importResult.created} new and {importResult.updated} updated shifts.
            {' '}Presence: {importResult.presenceCreated || 0} new and {importResult.presenceUpdated || 0} updated shifts.
            {importResult.reconciliation && <div>
              Schedule reconciliation: {importResult.reconciliation.missing} missing, {importResult.reconciliation.hidden} newly hidden,
              {' '}{importResult.reconciliation.restored} restored, {importResult.reconciliation.review} to review in Performance.
            </div>}
            {' '}Created {importResult.usersCreated} staff users.
            {' '}Skipped {importResult.skippedUnmappedDepartment} shifts whose departments are not mapped.
            {' '}Skipped {importResult.skippedDisabledDepartment || 0} shifts with both imports disabled.
          </Alert>
        )}
        {shifts !== null && (
          <pre style={{
            background: '#f5f5f5',
            marginTop: 20,
            maxHeight: 520,
            overflow: 'auto',
            padding: 16,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {JSON.stringify(shifts, null, 2)}
          </pre>
        )}
      </Paper>
      <Paper sx={{maxWidth: 900, padding: 3, marginTop: 3}}>
        <Typography variant="h6" gutterBottom>Schedule</Typography>
        <Typography paragraph>
          Import all scheduled shifts for this week and next week, from Monday through Sunday. Only departments assigned to a site are imported.
        </Typography>
        {scheduleError && <Alert severity="error" sx={{marginBottom: 2}}>{scheduleError}</Alert>}
        <Button variant="contained" onClick={importSchedule} disabled={scheduleLoading || importLoading || payLoading || !status?.connected}>
          {scheduleLoading ? 'Importing schedule...' : 'Import schedule'}
        </Button>
        {schedule !== null && (
          <Alert severity="success" sx={{marginTop: 2}}>
            Rota: {schedule.created} new and {schedule.updated} updated shifts.
            {' '}Presence: {schedule.presenceCreated || 0} new and {schedule.presenceUpdated || 0} updated shifts.
            {schedule.absences && <> Absences: {schedule.absences.saved || 0} saved{schedule.absences.skippedMissingUser ? ` · ${schedule.absences.skippedMissingUser} without a linked staff account` : ''}.</>}
            {schedule.reconciliation && <div>
              Schedule reconciliation: {schedule.reconciliation.missing} missing, {schedule.reconciliation.hidden} newly hidden,
              {' '}{schedule.reconciliation.restored} restored, {schedule.reconciliation.review} to review in Performance.
            </div>}
            {' '}Created {schedule.usersCreated} staff users.
            {' '}Skipped {schedule.skippedUnmappedDepartment} shifts whose departments are not mapped.
            {' '}Skipped {schedule.skippedDisabledDepartment || 0} shifts with both imports disabled.
          </Alert>
        )}
      </Paper>
    </div>
  );
}
